/**
 * MACONIA 2026 brand tokens, for the places that need a colour in code rather than
 * in a Tailwind class — MUI `sx`, ApexCharts series, canvas, inline SVG fills.
 *
 * This is a mirror of theme/brand.js, which is the canonical source and what
 * Tailwind reads. src/theme/tokens.spec.ts asserts the two are identical, because a
 * colour that is right in Tailwind and stale here shows up in exactly one component
 * and nowhere else.
 *
 * See docs/brand-implementation-guide.md.
 */

/** The five colours the brand specifies. Everything else is derived from them. */
export const BRAND = {
	ink: "#11110F",
	text: "#1B1B1B",
	subtle: "#686761",
	white: "#FFFFFF",
	accent: "#FF5A24",
} as const;

/**
 * The accent in its three forms. `accent` is a fill (3.12:1 on white — never text),
 * `accentDark` a hover fill (4.39:1 — a near miss is still a miss), `accentStrong`
 * the only orange you may write with (5.45:1).
 */
export const ACCENT = {
	accent: BRAND.accent,
	accentDark: "#D9440F",
	accentTint: "#FFEDE6",
	accentStrong: "#B8431A",
} as const;

/** The warm ramp. Every neutral leans yellow, the way #686761 does. */
export const NEUTRAL = {
	900: BRAND.ink,
	700: "#4A4944",
	600: BRAND.subtle,
	500: "#8C8B85",
	400: "#A3A19A",
	350: "#C4C1BA",
	300: "#DCDAD6",
	200: "#E8E6E2",
	100: "#EFEEEB",
	50: "#F4F3F1",
	25: "#FAF9F8",
} as const;

export const SURFACE = {
	page: BRAND.white,
	alt: NEUTRAL[50],
	wash: NEUTRAL[25],
	border: NEUTRAL[300],
	borderSoft: NEUTRAL[200],
} as const;

/** Warning is amber. Orange belongs to the call to action alone. */
export const SIGNAL = {
	success: "#3DA318",
	warning: "#D9A400",
	danger: "#DF2800",
	successLight: "#DAF8E6",
	warningLight: "#FEEAA3",
	errorLight: "#FFD9DA",
	warningInk: "#7A5300",
} as const;

/** Colour that encodes a value rather than an intent — charts, matrices, categories. */
export const DATA = {
	severityNone: "#8C8B85",
	severityLow: "#198754",
	severityLowMedium: "#A6C13C",
	severityMedium: "#EAB308",
	severityHigh: "#B8431A",
	severityCritical: "#DC3545",
	plum: "#6B4FA0",
} as const;

/**
 * Ordered low → critical, for charts and risk matrices. `severityLowMedium` and
 * `severityMedium` are fill-only (2.04:1 and 1.92:1) — put ink on them, never white.
 */
export const SEVERITY_SCALE = [
	DATA.severityNone,
	DATA.severityLow,
	DATA.severityLowMedium,
	DATA.severityMedium,
	DATA.severityHigh,
	DATA.severityCritical,
] as const;

/**
 * Six fills dark enough for white initials and distinguishable at 24px. Deliberately
 * not the accent — an avatar is not a call to action.
 */
export const AVATAR_FILLS = [
	"#B8431A",
	"#11110F",
	"#686761",
	"#226B0D",
	"#7A5300",
	"#6B4FA0",
] as const;

/** Pick a stable avatar fill for a name or id. */
export const avatarFill = (seed: string): string => {
	let hash = 0;

	for (let i = 0; i < seed.length; i++) {
		hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
	}

	return AVATAR_FILLS[hash % AVATAR_FILLS.length];
};

/** 6px buttons and inputs, 10px cards, 20px hero, pills. */
export const RADIUS = {
	button: "6px",
	input: "6px",
	card: "10px",
	hero: "20px",
	pill: "9999px",
} as const;

/** Soft and low-contrast — a lift, not a drop. */
export const ELEVATION = {
	raised: "6px 6px 18px rgba(0,0,0,0.3)",
	raisedHover: "4px 5px 11px -1px rgba(0,0,0,0.3)",
	flat: "none",
} as const;
